import { useState } from 'react'
import {
  ArrowLeft,
  ArrowDownUp,
  BadgeCheck,
  FileWarning,
  Quote,
  ShieldCheck,
  Undo2,
  XCircle,
} from 'lucide-react'
import type {
  FreigabeEntscheidValue,
  KbFreigabeAuditEintrag,
  KbFreigabeDokument,
} from '@mindkeep/shared'
import {
  useKbFreigabeDetail,
  useKbFreigabeEntscheid,
  useKbFreigaben,
} from '@/features/kb/use-kb-freigaben'
import { useWorkspaceRole } from '@/features/workspace/use-workspace-role'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/page-header'
import { EmptyState } from '@/components/empty-state'
import { ErrorState } from '@/components/error-state'
import { SkeletonList } from '@/components/skeleton-list'

/**
 * Freigabe-Ansicht (RNA M5).
 *
 * Der Retrieval-Filter aus M2 laesst nur `freigegeben` in eine Antwort. Diese
 * Seite ist die andere Haelfte: Entwuerfe sind hier sichtbar — und nur hier —
 * und eine Fachperson entscheidet ueber jedes einzelne. Drei Aktionen, keine
 * Sammelauswahl, kein «alle freigeben».
 */

const STATUS_LABEL: Record<string, string> = {
  entwurf: 'Entwurf',
  in_pruefung: 'In Pruefung',
  freigegeben: 'Freigegeben',
  zurueckgezogen: 'Zurueckgewiesen',
}

const ENTSCHEID_LABEL: Record<FreigabeEntscheidValue, string> = {
  freigegeben: 'freigegeben',
  zurueckgewiesen: 'zurueckgewiesen',
  ueberarbeitung: 'zur Ueberarbeitung zurueckgegeben',
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'freigegeben') {
    return <Badge className="bg-success text-success-foreground">Freigegeben</Badge>
  }
  if (status === 'zurueckgezogen') {
    return <Badge variant="destructive">Zurueckgewiesen</Badge>
  }
  return <Badge variant="secondary">{STATUS_LABEL[status] ?? status}</Badge>
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function FreigabenPage() {
  const [sort, setSort] = useState<'asc' | 'desc'>('desc')
  const [selected, setSelected] = useState<string | null>(null)
  const list = useKbFreigaben(sort)
  const { canReview, isLoading: roleLoading } = useWorkspaceRole()

  if (selected) {
    return (
      <FreigabeDetail path={selected} canReview={canReview} onBack={() => setSelected(null)} />
    )
  }

  const dokumente = list.data?.dokumente ?? []
  const offen = dokumente.filter((doc) => doc.status !== 'freigegeben')
  const freigegeben = dokumente.filter((doc) => doc.status === 'freigegeben')

  return (
    // R1: gemeinsamer Flow-Arbeitsrahmen — max 1180px, mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
      <PageHeader
        title="Freigaben"
        description="Hier prüfst du Dokumente für die kuratierte Fachwissensbasis. Freigegebene Fassungen stehen in diesem Antwortpfad bereit."
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSort((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
        >
          <ArrowDownUp className="mr-2 h-4 w-4" />
          {sort === 'desc' ? 'Neueste zuerst' : 'Aelteste zuerst'}
        </Button>
      </PageHeader>

      {!roleLoading && !canReview && (
        <p className="mb-6 rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          Du kannst den Pruefstapel lesen, aber nicht entscheiden. Die Freigabe liegt bei den
          Fachverantwortlichen des Workspace.
        </p>
      )}

      {list.isLoading ? (
        <SkeletonList rows={5} />
      ) : list.isError ? (
        <ErrorState
          title="Pruefstapel konnte nicht geladen werden"
          description={list.error instanceof Error ? list.error.message : 'Bitte versuche es erneut.'}
          onRetry={() => void list.refetch()}
          isRetrying={list.isFetching}
        />
      ) : dokumente.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="Keine Dokumente zur Pruefung"
          description="Sobald ein kuratiertes Wissensdokument abgelegt wird, erscheint es hier als Entwurf."
        />
      ) : (
        <div className="space-y-8">
          <section className="space-y-3">
            <h2 className="text-[16px] font-[650] tracking-tight text-foreground">
              Offen ({offen.length})
            </h2>
            {offen.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nichts offen.</p>
            ) : (
              offen.map((doc) => (
                <DokumentZeile key={doc.path} dokument={doc} onOpen={() => setSelected(doc.path)} />
              ))
            )}
          </section>

          {freigegeben.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-[16px] font-[650] tracking-tight text-foreground">
                Freigegeben ({freigegeben.length})
              </h2>
              {freigegeben.map((doc) => (
                <DokumentZeile key={doc.path} dokument={doc} onOpen={() => setSelected(doc.path)} />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function DokumentZeile({
  dokument,
  onOpen,
}: {
  dokument: KbFreigabeDokument
  onOpen: () => void
}) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-4 p-4">
        <div className="min-w-0 flex-1 basis-40">
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 break-words text-sm font-medium text-foreground">{dokument.titel}</p>
            <StatusBadge status={dokument.status} />
            {dokument.doc_type && (
              <Badge variant="outline" className="break-all">{dokument.doc_type}</Badge>
            )}
          </div>
          <p className="break-all font-mono text-xs text-muted-foreground">{dokument.path}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Geaendert am {formatDate(dokument.geaendert_am)} · {dokument.chunks_count} Ausschnitte
            {dokument.durch && dokument.entscheid
              ? ` · zuletzt ${ENTSCHEID_LABEL[dokument.entscheid]} von ${dokument.durch}`
              : ''}
          </p>
          {dokument.begruendung && (
            <p className="mt-1 border-l-2 border-brand-warning pl-2 text-xs text-muted-foreground">
              {dokument.begruendung}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onOpen}>
          Pruefen
        </Button>
      </CardContent>
    </Card>
  )
}

function FreigabeDetail({
  path,
  canReview,
  onBack,
}: {
  path: string
  canReview: boolean
  onBack: () => void
}) {
  const detail = useKbFreigabeDetail(path)
  const entscheid = useKbFreigabeEntscheid()
  const [dialog, setDialog] = useState<'zurueckgewiesen' | 'ueberarbeitung' | null>(null)
  const [begruendung, setBegruendung] = useState('')

  function submit(wert: FreigabeEntscheidValue, text?: string) {
    entscheid.mutate(
      { path, entscheid: wert, ...(text ? { begruendung: text } : {}) },
      {
        onSuccess: () => {
          setDialog(null)
          setBegruendung('')
          onBack()
        },
      },
    )
  }

  const daten = detail.data

  return (
    // R1: Detail-Lesebreite 720px, mobil 16px Rand.
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 min-[761px]:px-6 min-[761px]:py-8">
      <Button variant="ghost" size="sm" className="mb-4" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Zurueck zum Pruefstapel
      </Button>

      {detail.isLoading ? (
        <SkeletonList rows={4} />
      ) : detail.isError || !daten ? (
        <ErrorState
          title="Dokument konnte nicht geladen werden"
          description={
            detail.error instanceof Error ? detail.error.message : 'Bitte versuche es erneut.'
          }
          onRetry={() => void detail.refetch()}
          isRetrying={detail.isFetching}
        />
      ) : (
        <div className="space-y-6">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[26px] font-[650] tracking-tight break-words text-foreground">
                {daten.dokument.titel}
              </h1>
              <StatusBadge status={daten.dokument.status} />
            </div>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{daten.dokument.path}</p>
          </div>

          <Card>
            <CardContent className="space-y-3 p-6">
              <div className="flex items-center gap-2 text-[16px] font-[650] text-foreground">
                <Quote className="h-4 w-4 text-muted-foreground" />
                Quellen
              </div>
              <p className="break-all rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground">
                {daten.quellen.zitatlabel}
              </p>
              <p className="text-xs text-muted-foreground">
                So wuerde dieses Dokument in einer Antwort zitiert.
              </p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 pt-2 text-sm sm:grid-cols-3">
                <Feld label="Quellenlage" wert={daten.quellen.quellenlage} />
                <Feld label="Tier" wert={daten.quellen.tier === null ? null : String(daten.quellen.tier)} />
                <Feld label="Rechtsraum" wert={daten.quellen.rechtsraum} />
                <Feld label="Normfamilie" wert={daten.quellen.normfamilie} />
                <Feld label="Ziffer" wert={daten.quellen.ziffer} />
                <Feld label="Belastbarkeit" wert={daten.quellen.belastbarkeit} />
                <Feld label="OCR-Qualitaet" wert={daten.quellen.ocr_qualitaet} />
                <Feld label="Gueltig von" wert={daten.quellen.gueltig_von} />
                <Feld label="Gueltig bis" wert={daten.quellen.gueltig_bis} />
              </dl>
              {daten.quellen.verweise.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs font-medium text-muted-foreground">Verweise</p>
                  <ul className="mt-1 space-y-0.5">
                    {daten.quellen.verweise.map((verweis) => (
                      <li key={verweis} className="break-all font-mono text-xs text-brand-accent">
                        [[{verweis}]]
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <section className="space-y-3">
            <h2 className="text-[16px] font-[650] tracking-tight text-foreground">
              Ausschnitte ({daten.chunks.length})
            </h2>
            {daten.chunks.length === 0 ? (
              <EmptyState
                icon={FileWarning}
                title="Keine Ausschnitte im Index"
                description="Das Dokument ist abgelegt, wurde aber noch nicht gechunkt. Ohne Ausschnitte kann es in keiner Antwort erscheinen."
              />
            ) : (
              daten.chunks.map((chunk) => (
                <Card key={chunk.chunk_index}>
                  <CardContent className="space-y-2 p-6">
                    <p className="break-all font-mono text-xs text-muted-foreground">
                      {chunk.section_path || `Ausschnitt ${chunk.chunk_index + 1}`}
                      {chunk.contains_table && ' · enthaelt eine Tabelle'}
                    </p>
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                      {chunk.chunk_text}
                    </pre>
                  </CardContent>
                </Card>
              ))
            )}
          </section>

          {daten.verlauf.length > 0 && <Verlauf eintraege={daten.verlauf} />}

          {canReview && (
            <div className="flex flex-wrap gap-2 border-t pt-6">
              <Button
                onClick={() => submit('freigegeben')}
                disabled={entscheid.isPending}
                className="bg-success text-success-foreground hover:bg-brand-success/90"
              >
                <BadgeCheck className="mr-2 h-4 w-4" />
                Freigeben
              </Button>
              <Button
                variant="outline"
                onClick={() => setDialog('zurueckgewiesen')}
                disabled={entscheid.isPending}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Zurueckweisen
              </Button>
              <Button
                variant="outline"
                onClick={() => setDialog('ueberarbeitung')}
                disabled={entscheid.isPending}
              >
                <Undo2 className="mr-2 h-4 w-4" />
                Zur Ueberarbeitung
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null)
            setBegruendung('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog === 'zurueckgewiesen' ? 'Dokument zurueckweisen' : 'Zur Ueberarbeitung zurueckgeben'}
            </DialogTitle>
            <DialogDescription>
              {dialog === 'zurueckgewiesen'
                ? 'Das Dokument bleibt aus Antworten ausgeschlossen. Die Begruendung wird ins Frontmatter geschrieben und protokolliert.'
                : 'Das Dokument geht als Entwurf zurueck an die verfassende Person und bleibt aus Antworten ausgeschlossen.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="begruendung">Begruendung</Label>
            <Textarea
              id="begruendung"
              rows={4}
              value={begruendung}
              onChange={(event) => setBegruendung(event.target.value)}
              placeholder="Was fehlt oder was ist falsch?"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setDialog(null)
                setBegruendung('')
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => dialog && submit(dialog, begruendung.trim())}
              disabled={begruendung.trim().length === 0 || entscheid.isPending}
            >
              Bestaetigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Feld({ label, wert }: { label: string; wert: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{wert ?? '—'}</dd>
    </div>
  )
}

function Verlauf({ eintraege }: { eintraege: KbFreigabeAuditEintrag[] }) {
  return (
    <section className="space-y-3">
      <h2 className="text-[16px] font-[650] tracking-tight text-foreground">Entscheidungsverlauf</h2>
      <p className="text-xs text-muted-foreground">
        Unveraenderlich protokolliert. Eintraege werden nie geaendert oder geloescht.
      </p>
      <Card>
        <CardContent className="divide-y p-0">
          {eintraege.map((eintrag) => (
            <div key={eintrag.id} className="space-y-1 p-4">
              <p className="text-sm text-foreground">
                {eintrag.durch ?? 'Unbekannt'} hat das Dokument{' '}
                {eintrag.entscheid ? ENTSCHEID_LABEL[eintrag.entscheid] : 'entschieden'}.
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(eintrag.created_at)}
                {eintrag.status_vorher && eintrag.status_nachher
                  ? ` · ${STATUS_LABEL[eintrag.status_vorher] ?? eintrag.status_vorher} → ${
                      STATUS_LABEL[eintrag.status_nachher] ?? eintrag.status_nachher
                    }`
                  : ''}
              </p>
              {eintrag.begruendung && (
                <p className="border-l-2 border-border pl-2 text-sm text-muted-foreground">
                  {eintrag.begruendung}
                </p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  )
}
